import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export const popoverPosition = (rect: Pick<DOMRect, 'left' | 'bottom' | 'top' | 'width'>, viewportWidth: number, viewportHeight: number, preferredWidth: number): {width:number;left:number;maxHeight:number;top?:number;bottom?:number} => {
    const margin = 8;
    const width = Math.max(0, Math.min(preferredWidth, viewportWidth - margin * 2));
    const below = Math.max(0, viewportHeight - rect.bottom - margin * 2);
    const above = Math.max(0, rect.top - margin * 2);
    const up = below < 200 && above > below;
    return { width, left: Math.max(margin, Math.min(rect.left, viewportWidth - width - margin)),
        maxHeight: Math.min(480, up ? above : below),
        ...(up ? {bottom: Math.max(margin, viewportHeight - rect.top + margin)} : {top: Math.max(margin, rect.bottom + margin)}) };
};

type Props = React.HTMLAttributes<HTMLDivElement> & {
    anchor: () => HTMLElement | null;
    onClose: () => void;
    width?: number;
};

/** Portals avoid clipping by table containers and navbar scroll regions. */
export default function ViewportPopover({anchor, onClose, width = 320, children, style, ...props}: Props) {
    const panel = useRef<HTMLDivElement>(null);
    const callbacks = useRef({anchor, onClose});
    callbacks.current = {anchor, onClose};
    const [position, setPosition] = useState<React.CSSProperties>({visibility: 'hidden'});
    useLayoutEffect(() => {
        const update = () => {
            const element = callbacks.current.anchor();
            if (!element) return;
            const viewport = window.visualViewport;
            const viewportWidth = viewport?.width || window.innerWidth;
            const viewportHeight = viewport?.height || window.innerHeight;
            const offsetLeft = viewport?.offsetLeft || 0;
            const offsetTop = viewport?.offsetTop || 0;
            const rect = element.getBoundingClientRect();
            const layout = popoverPosition({left: rect.left - offsetLeft, top: rect.top - offsetTop,
                bottom: rect.bottom - offsetTop, width: rect.width}, viewportWidth, viewportHeight, width);
            setPosition({...layout, bottom: undefined, left: layout.left + offsetLeft,
                top: (layout.top !== undefined ? layout.top : viewportHeight - layout.bottom!) + offsetTop,
                transform: layout.bottom !== undefined ? 'translateY(-100%)' : undefined});
        };
        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        window.visualViewport?.addEventListener('resize', update);
        window.visualViewport?.addEventListener('scroll', update);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
            window.visualViewport?.removeEventListener('resize', update);
            window.visualViewport?.removeEventListener('scroll', update);
        };
    }, [width]);
    useEffect(() => {
        const outside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (!panel.current?.contains(target) && !callbacks.current.anchor()?.contains(target)) callbacks.current.onClose();
        };
        const key = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                const element = callbacks.current.anchor();
                callbacks.current.onClose();
                if (!element?.contains(event.target as Node)) {
                    (element?.querySelector('button,input') as HTMLElement || element)?.focus();
                }
            }
        };
        document.addEventListener('mousedown', outside);
        document.addEventListener('keydown', key);
        return () => {document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', key);};
    }, []);
    return createPortal(<div {...props} ref={panel} style={{...style, ...position, position:'fixed', zIndex:10000, fontFamily:"'DM Sans', sans-serif",
        overflowY:'auto', overflowX:'hidden', overscrollBehavior:'contain', boxSizing:'border-box', overflowWrap:'anywhere'}}>{children}</div>, document.body);
}
